import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { workorderSearchOptions } from "@/api/workorders/query-options";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface WorkorderPickerProps {
  value: { id: string; label: string } | null;
  onChange: (v: { id: string; label: string } | null) => void;
  placeholder?: string;
  className?: string;
}

export function WorkorderPicker({ value, onChange, placeholder, className }: WorkorderPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [debouncedQ, setDebouncedQ] = React.useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(timer);
  }, [q]);

  const { data: results = [] } = useQuery({
    ...workorderSearchOptions(debouncedQ),
    enabled: true,
  });

  const triggerLabel = value?.label ?? placeholder ?? t("workorder.picker.placeholder");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("workorder.picker.placeholder")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList>
            <CommandEmpty>{t("workorder.picker.empty")}</CommandEmpty>
            {results.length > 0 && (
              <CommandGroup>
                {results.map((wo) => {
                  const label = `${wo.erpCode} — ${wo.principal}`;
                  const isSelected = value?.id === wo.id;
                  return (
                    <CommandItem
                      key={wo.id}
                      value={wo.id}
                      onSelect={() => {
                        onChange({ id: wo.id, label });
                        setOpen(false);
                        setQ("");
                      }}
                    >
                      <Check
                        className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{wo.erpCode}</span>
                        <span className="text-xs text-ink-500">{wo.principal}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
